import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Scene, User } from '../types';
import type { LucideIcon } from 'lucide-react';
import { Plus, Search, SlidersHorizontal, Boxes, Calendar, Grid, List,
  ChevronRight, ImageIcon, Clock, Share2, Download, Cuboid, Trash2, Trophy, ChevronDown, ChevronUp, Users, Heart, Award, Flame, Star, Sparkles, Eye } from 'lucide-react';
import NewSceneForm from './NewSceneForm';
import SceneCard from './SceneCard';
import SceneDetailsPanel from './SceneDetailsPanel';
import ReconstructionFeedback from './ReconstructionFeedback';
import Toast from './Toast';
import { useAuth } from '../contexts/AuthContext';
import { customerApi, ShareListItem } from '../services/customerApi';
import { s3CredentialsManager } from '../services/s3CredentialsManager.service';
import { useNavigate } from 'react-router-dom';
import { useUploadCtx } from '../contexts/UploadContext';
import { s3MetadataReader } from '../services/s3MetadataReader.service';
import { scanLifecycleService } from '../services/scanLifecycle.service';
import logo from '../assets/logo.gif';

interface Props {
  showNav?: boolean;
  scenes: Scene[];
  isLoadingScenes?: boolean;
  scenesError?: string | null;
  onRefreshScans?: () => void;
  onSceneSelect: (scene: Scene) => void;
  onCreateScene: (sceneData: Omit<Scene, 'id' | 'images' | 'quality' | 'thumbnail'>) => void;
  onShare: (scene: Scene) => void;
  onDownload: (scene: Scene) => void;
  onDelete: (scene: Scene) => void;
  onRate: () => void;
  showFeedback: boolean;
  setShowFeedback: (show: boolean) => void;
  selectedScene: Scene | null;
  onSubmitFeedback: (feedback: any) => void;
  user: User | null;
  onLogout: () => void;
  sceneLoadingStates?: Record<string, boolean>;
  loadingMessage?: string;
}

// Declare the handleBack function properly
function handleBack() {
  // This is just a placeholder - the real implementation would be provided by parent components
  console.log("Back navigation");
}

interface CommunityScene extends Omit<Scene, 'images'> {
  author: string;
  likes: number;
  downloads: number;
  isLiked?: boolean;
  tags: string[];
}

interface SharedScene extends Scene {
  sharedBy: string;
  sharedByName?: string;
  sharedAt: Date;
  shareMessage?: string;
  tags: string[];
}

export default function Dashboard({ 
  showNav, 
  scenes, 
  isLoadingScenes, 
  scenesError, 
  onRefreshScans, 
  onSceneSelect, 
  onCreateScene, 
  onShare, 
  onDownload, 
  onDelete, 
  onRate, 
  showFeedback, 
  setShowFeedback, 
  selectedScene, 
  onSubmitFeedback, 
  user,
  onLogout,
  sceneLoadingStates = {},
  loadingMessage = ''
}: Props) {
  // Get user role from auth context
  const { role, user: authUser } = useAuth();
  
  // Check if user can create scenes (exclude waitlisted and viewer roles)
  const canCreateScenes = role && !['waitlisted', 'viewer'].includes(role);
  
  // Check if user can share scenes (only creators and admins)
  const canShareScenes = ['creator', 'admin'].includes(role ?? '');
  
  // Determine available tabs based on role
  const availableTabs = useMemo(() => {
  switch (role) {
    case 'admin':
      return ['my-collections', 'shared-collections', 'cr3ators-collections'];
    case 'creator':
      return ['my-collections', 'shared-collections'];
    case 'viewer':
      return ['shared-collections'];
    default:
      return [];
  }
}, [role]);
  
  // Tab state - default based on role
  const [activeTab, setActiveTab] = useState<'my-collections' | 'shared-collections' | 'cr3ators-collections'>(
  role === 'admin' ? 'my-collections' :
  role === 'creator' ? 'my-collections' :
  'shared-collections'
);
  
  // My Scenes state variables
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'quality' | 'images'>('date');
  const [filterStatus, setFilterStatus] = useState<Scene['status'] | 'all'>('all');
  const [showNewSceneForm, setShowNewSceneForm] = useState(false);
  const [sceneForFeedback, setSceneForFeedback] = useState<Scene | null>(null);
  
  // Toast notification state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info'; isVisible: boolean }>({
    message: '',
    type: 'info',
    isVisible: false
  });

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToast({ message, type, isVisible: true });
  };

  const handleToastClose = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };
  
  // State for delete confirmation
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [sceneToDelete, setSceneToDelete] = useState<Scene | null>(null);
  
  // Shared Collections state
  const [sharedSearchTerm, setSharedSearchTerm] = useState('');
  const [sharedSortBy, setSharedSortBy] = useState<'date' | 'quality' | 'images' | 'shareDate'>('shareDate');
  const [sharedFilterStatus, setSharedFilterStatus] = useState<Scene['status'] | 'all'>('all');
  const [isLoadingShares, setIsLoadingShares] = useState(false);
  const [sharesError, setSharesError] = useState<string | null>(null);
  const [receivedShares, setReceivedShares] = useState<ShareListItem[]>([]);
  const [sharedScenes, setSharedScenes] = useState<SharedScene[]>([]);
  
  //cr3ators collection state
  const [cr3atorsSearchTerm, setCr3atorsSearchTerm] = useState('');
  const [cr3atorsSortBy, setCr3atorsSortBy] = useState<'date' | 'quality' | 'images'>('date');
  const [cr3atorsFilterStatus, setCr3atorsFilterStatus] = useState<Scene['status'] | 'all'>('all');

  // UI toggle state
  const [showMyScenes, setShowMyScenes] = useState(true);
  
  // CONSOLIDATED LOADING STATE MANAGEMENT
  const [activeLoadingSceneId, setActiveLoadingSceneId] = useState<string | null>(null);
  const [loadingAction, setLoadingAction] = useState<'select' | 'share' | 'download' | 'rate' | 'trigger' | null>(null);
  const currentOperationRef = useRef<{ sceneId: string; action: string } | null>(null);
  
  // Upload context
  const { reset: resetUuid } = useUploadCtx();
  
  // Computed loading states
  const isAnySceneLoading = activeLoadingSceneId !== null;
  const isGloballyDisabled = isAnySceneLoading || isLoadingScenes || isLoadingShares;

  // Get user's display name for the title
  const getUserName = () => {
    // Try to get studioName (company name) first, then fall back to full name
    if (user?.studioName && user.studioName !== 'Personal Account') return user.studioName;
    if (user?.name) return user.name; // Use full name
    if (authUser?.displayName) return authUser.displayName; // Use full displayName
    if (user?.email) return user.email.split('@')[0] + "'s Studio"; // Get username from email
    if (authUser?.email) return authUser.email.split('@')[0] + "'s Studio"; // Get username from email
    return 'Your'; // Fallback
  };

  // Helper function to start a loading operation
  const startLoadingOperation = (sceneId: string, action: 'select' | 'share' | 'download' | 'rate' | 'trigger') => {
    console.log(`🔄 Starting ${action} operation for scene:`, sceneId);
    setActiveLoadingSceneId(sceneId);
    setLoadingAction(action);
    currentOperationRef.current = { sceneId, action };
  };

  // Helper function to end a loading operation
  const endLoadingOperation = (sceneId: string, action: string) => {
    // Only clear if this is still the current operation
    if (currentOperationRef.current?.sceneId === sceneId && currentOperationRef.current?.action === action) {
      console.log(`✅ Ending ${action} operation for scene:`, sceneId);
      setActiveLoadingSceneId(null);
      setLoadingAction(null);
      currentOperationRef.current = null;
    }
  };

  // Scene selection handler with proper loading management
  const handleMySceneSelect = async (scene: Scene) => {
    console.log('🔍 handleMySceneSelect called for:', scene.id);
    
    // Prevent multiple simultaneous operations
    if (isGloballyDisabled) {
      console.log('⚠️ Operation blocked - system is busy:', { 
        activeLoadingSceneId, 
        isLoadingScenes, 
        isLoadingShares 
      });
      return;
    }
    
    startLoadingOperation(scene.id, 'select');
    
    try {
      await onSceneSelect(scene);
    } catch (error) {
      console.error('❌ Scene selection failed:', error);
    } finally {
      endLoadingOperation(scene.id, 'select');
    }
  };

  // Shared scene selection handler
  const handleSharedSceneSelect = async (scene: SharedScene) => {
    if (isGloballyDisabled) {
      console.log('⚠️ Shared scene selection blocked - system is busy');
      return;
    }
    
    startLoadingOperation(scene.id, 'select');
    
    try {
      console.log('Viewing shared scene:', scene.name);
      await onSceneSelect(scene);
    } catch (error) {
      console.error('❌ Shared scene selection failed:', error);
    } finally {
      endLoadingOperation(scene.id, 'select');
    }
  };

  // Share handler with loading protection
  const handleShare = async (scene: Scene) => {
    if (isGloballyDisabled) {
      console.log('⚠️ Share operation blocked - system is busy');
      return;
    }
    
    startLoadingOperation(scene.id, 'share');
    
    try {
      await onShare(scene);
    } catch (error) {
      console.error('❌ Share operation failed:', error);
    } finally {
      endLoadingOperation(scene.id, 'share');
    }
  };

  // Download handler with loading protection
  const handleDownload = async (scene: Scene) => {
    if (isGloballyDisabled) {
      console.log('⚠️ Download operation blocked - system is busy');
      return;
    }
    
    startLoadingOperation(scene.id, 'download');
    
    try {
      await onDownload(scene);
    } catch (error) {
      console.error('❌ Download operation failed:', error);
    } finally {
      endLoadingOperation(scene.id, 'download');
    }
  };

  // Rate handler with loading protection
  const handleRateClick = (scene: Scene) => {
    if (isGloballyDisabled) {
      console.log('⚠️ Rate operation blocked - system is busy');
      return;
    }
    
    setSceneForFeedback(scene);
    setShowFeedback(true);
  };

  // Delete scene handling with confirmation
  const handleDeleteClick = (scene: Scene) => {
    setSceneToDelete(scene);
    setShowDeleteConfirmModal(true);
  };
  
  const handleConfirmDelete = () => {
    if (sceneToDelete) {
      onDelete(sceneToDelete);
      setSceneToDelete(null);
      setShowDeleteConfirmModal(false);
    }
  };
  
  const handleCancelDelete = () => {
    setSceneToDelete(null);
    setShowDeleteConfirmModal(false);
  };

  // Shared scene handlers with loading protection
  const handleSharedSceneShare = async (scene: SharedScene) => {
    if (isGloballyDisabled) return;
    
    startLoadingOperation(scene.id, 'share');
    try {
      console.log('Re-sharing scene:', scene.name);
      await onShare(scene);
    } catch (error) {
      console.error('❌ Re-share operation failed:', error);
    } finally {
      endLoadingOperation(scene.id, 'share');
    }
  };

  const handleSharedSceneDownload = async (scene: SharedScene) => {
    if (isGloballyDisabled) return;
    
    startLoadingOperation(scene.id, 'download');
    try {
      console.log('Downloading scene:', scene.name);
      await onDownload(scene);
    } catch (error) {
      console.error('❌ Download operation failed:', error);
    } finally {
      endLoadingOperation(scene.id, 'download');
    }
  };

  // Trigger reconstruction handler
  const handleTriggerReconstruction = async (scene: Scene) => {
    if (isGloballyDisabled) {
      console.log('⚠️ Trigger reconstruction blocked - system is busy');
      return;
    }

    startLoadingOperation(scene.id, 'trigger');

    try {
      // Fetch the up-to-date Scan details from backend
      const scan = await customerApi.getScanById(scene.id);

      if (!scan || !scan.s3FolderPath) {
        throw new Error('Scan data missing s3FolderPath');
      }

      const folderPath = scan.s3FolderPath.replace(/\/$/, '');

      // Read sessionMetadata.json from S3
      const parsed = await s3MetadataReader.readAndValidateForReconstruction(folderPath);

      // Kick off reconstruction workflow (standardize preset source to capturePreset)
      await scanLifecycleService.completeReconstructionWorkflow(
        scan.scanId,
        parsed.sessionUUID,
        parsed.sequences,
        scene.capturePreset
      );

      console.log('✅ Reconstruction triggered successfully');
      console.log('🎯 [CRITICAL] About to call onRefreshScans, authUser:', !!authUser);


      // Optional: refresh list after short delay
      onRefreshScans?.();

      console.log('✅ [CRITICAL] onRefreshScans called - waiting for response...');

    } catch (error) {
      console.error('❌ Trigger reconstruction failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      showToast(`Reconstruction failed: ${errorMessage}`, 'error');
    } finally {
      endLoadingOperation(scene.id, 'trigger');
    }
  };

  // Auto-load shared content for viewers on mount
  useEffect(() => {
    if (role === 'viewer' && authUser) {
      loadSharedContent();
    }
  }, [role, authUser]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
  return () => {
    // Cleanup any active sessions when dashboard unmounts
    console.log('[Dashboard] Component unmounting - cleaning up all sessions');
    s3CredentialsManager.cleanupAllSessions().catch(err => 
      console.warn('[Dashboard] Failed to cleanup sessions on unmount:', err)
    );
  };
}, []);

// Handle download scan app button click
const handleDownloadScanApp = () => {
 window.open('https://apps.apple.com/us/app/cr3ate-ai/id6751804720', '_blank');
};
  
  // Load shared content
  const loadSharedContent = async () => {
    if (!authUser) return;

    setIsLoadingShares(true);
    setSharesError(null);

    try {
      // Fetch shares received by the current user
      const sharesResponse = await customerApi.getSharesReceived({
        status: 'accepted',
        limit: 100
      });

      const shares = sharesResponse.data.shares;
      setReceivedShares(shares);

      // Build scenes with real scan metadata for accurate thumbnail & image count
      const scenes: SharedScene[] = await Promise.all(
        shares.map(async (share) => {
            let scanData: any | null = null;
            try {
              scanData = await customerApi.getScanById(share.scanId);
            } catch (_err) {
              console.warn(`Failed to fetch scan data for ${share.scanId}`, _err);
            }

            return {
              id: share.scanId,
              name: share.scanName || scanData?.title || `Shared Scene - ${share.scanId.slice(0, 8)}`,
              description: share.message || scanData?.description || 'A 3D scene shared with you',
              type: (scanData?.category as Scene['type']) || 'unknown',
              cameraType: (scanData?.cameraType as Scene['cameraType']) || 'unknown',
              captureType: 'single' as const,
              createdAt: new Date(share.sharedAt).getTime(),
              updatedAt: scanData ? new Date(scanData.updatedAt).getTime() : new Date(share.sharedAt).getTime(),
              status: 'completed' as const,
              images: [],
              thumbnail: scanData?.thumbnailUrl,
              versions: [],
              quality: {
                overall: 0,
                imageCount: scanData?.inputImageCount || 0,
              },
              // ADD the missing required properties from Scene interface:
              creatorId: share.sharedBy, // Use sharedBy as the creator ID
              s3FolderPath: scanData?.s3FolderPath || undefined, // Include s3FolderPath if available
              ownerUsername: share.sharedByName || undefined, // Include owner username if available

              capturePreset: (['drive', 'indoor', 'object', 'outdoor_walk', 'drone', 'mix_drone', 'quick'].includes(scanData?.category || '')
              ? (scanData?.category as Scene['capturePreset'])
              : undefined),
                    
              // SharedScene specific properties:
              sharedBy: share.sharedBy,
              sharedByName: share.sharedByName,
              sharedAt: new Date(share.sharedAt),
              shareMessage: share.message,
              tags: []
            };
          })
        );

      setSharedScenes(scenes);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load shared content';
      setSharesError(errorMessage);
      console.error('Failed to load shared content:', error);
    } finally {
      setIsLoadingShares(false);
    }
  };

  // Load shared content when tab changes to shared collections or user changes (for non-viewers)
  useEffect(() => {
    if (activeTab === 'shared-collections' && authUser && role !== 'viewer') {
      loadSharedContent();
    }
  }, [activeTab, authUser, role]);

  // Create a properly formatted user object for UserMenu
  const formattedUser = user ? {
    name: user.name || user.email?.split('@')[0] || 'User',
    email: user.email || 'No email provided'
  } : null;

  // Filter My Scenes based on search and status
  const filteredScenes = scenes
  .filter(scene => {
    // Check if scene has any images
    const hasImages = scene.quality?.imageCount > 0;
    const isMine = scene.creatorId === authUser?.uid;
    console.log('[Check isMine]', {
    sceneId: scene.id,
    sceneName: scene.name,
    sceneOwner: scene.creatorId,
    currentUser: authUser?.uid,
    isMine,
    imageCount: scene.quality?.imageCount || 0,
    hasImages
  });
    const matchesSearch = scene.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          scene.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          scene.ownerUsername?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || scene.status === filterStatus;
    // Only return scenes with images that are mine and match search/status criteria
    return hasImages && isMine && matchesSearch && matchesStatus;
  })
    .sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return b.updatedAt - a.updatedAt;
        case 'quality':
          return b.quality.overall - a.quality.overall;
        case 'images':
          return b.quality.imageCount - a.quality.imageCount;
        default:
          return 0;
      }
    });

  // Filter Shared scenes based on search and status
  const filteredSharedScenes = sharedScenes
    .filter(scene => {
      const matchesSearch = scene.name.toLowerCase().includes(sharedSearchTerm.toLowerCase()) ||
                          scene.description?.toLowerCase().includes(sharedSearchTerm.toLowerCase()) ||
                          scene.shareMessage?.toLowerCase().includes(sharedSearchTerm.toLowerCase()) ||
                          scene.sharedByName?.toLowerCase().includes(sharedSearchTerm.toLowerCase());
      const matchesStatus = sharedFilterStatus === 'all' || scene.status === sharedFilterStatus;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      switch (sharedSortBy) {
        case 'shareDate':
          return b.sharedAt.getTime() - a.sharedAt.getTime();
        case 'date':
          return b.updatedAt - a.updatedAt;
        case 'quality':
          return b.quality.overall - a.quality.overall;
        case 'images':
          return b.quality.imageCount - a.quality.imageCount;
        default:
          return 0;
      }
    });

    const filteredOtherScenes = scenes
  .filter(scene => {
    // Check if scene has any images
    const hasImages = scene.quality?.imageCount > 0;
    const isByOthers = scene.creatorId !== authUser?.uid;
    console.log('[Check isByOthers]', {
      sceneId: scene.id,
      sceneName: scene.name,
      sceneOwner: scene.creatorId,
      currentUser: authUser?.uid,
      isByOthers,
      imageCount: scene.quality?.imageCount || 0,
      hasImages
    });
    const matchesSearch =
      scene.name.toLowerCase().includes(cr3atorsSearchTerm.toLowerCase()) ||
      scene.description?.toLowerCase().includes(cr3atorsSearchTerm.toLowerCase()) ||
      scene.ownerUsername?.toLowerCase().includes(cr3atorsSearchTerm.toLowerCase());
    const matchesStatus = cr3atorsFilterStatus === 'all' || scene.status === cr3atorsFilterStatus;
    return hasImages && isByOthers && matchesSearch && matchesStatus;
  })
  .sort((a, b) => {
    switch (cr3atorsSortBy) {
      case 'date':
        return b.updatedAt - a.updatedAt;
      case 'quality':
        return b.quality.overall - a.quality.overall;
      case 'images':
        return b.quality.imageCount - a.quality.imageCount;
      default:
        return 0;
    }
  });

  
  

  type TabKey = 'my-collections' | 'shared-collections' | 'cr3ators-collections';

  const tabConfig: Record<TabKey, { label: string; icon: LucideIcon }> = {
    'my-collections': { label: 'My Collections', icon: Boxes },
    'shared-collections': { label: 'Shared Collections', icon: Users },
    'cr3ators-collections': { label: 'Cr3ators Collections', icon: Sparkles }
  };

  return (
    <div className="min-h-screen bg-[#161513] overflow-x-hidden">
      {/* Global Loading Overlay - Match SceneManager spinner style */}
      {isAnySceneLoading && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 flex items-center justify-center">
          <div className="bg-[#161513] text-white rounded-xl shadow-xl p-6 flex items-center gap-4 max-w-sm mx-4 border border-[#0CB4E4]/20">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#0CB4E4]"></div>
            <div>
              <h3 className="font-medium text-white">
                {loadingAction === 'select' ? 'Looking up the scene 3D...' :
                 loadingAction === 'share' ? 'Sharing Scene...' :
                 loadingAction === 'download' ? 'Preparing Download...' :
                 loadingAction === 'rate' ? 'Opening Feedback...' :
                 'Processing...'}
              </h3>
              <p className="text-sm text-gray-300">Please wait</p>
            </div>
          </div>
        </div>
      )}
      
      {/* SceneDetailsPanel for 3D viewer */}
      {selectedScene && (
        <SceneDetailsPanel
          onClose={() => {}}
          model={{ 
            fileName: selectedScene.name,
            description: selectedScene.description,
            fileType: selectedScene.type,
            fileSize: selectedScene.fileSize,
            signedUrl: selectedScene.gaussModelPath,
            size: selectedScene.quality?.imageCount,
            captureType: selectedScene.captureType,
            cameraType: selectedScene.cameraType,
            createdAt: selectedScene.createdAt
          }} 
          {...(canShareScenes && selectedScene.creatorId === authUser?.uid ? { onShare: () => handleShare(selectedScene) } : {})}
          onDownload={() => handleDownload(selectedScene)}
          onDelete={() => handleDeleteClick(selectedScene)}
        />
      )}

      {/* Main Content with Logo Background */}
      <div className={`${showNav ? 'mt-8' : ''} relative`}>
        {/* Logo Background - spans across header area and content */}
        <div 
          className="absolute top-0 left-0 right-0 h-[400px] bg-cover bg-center bg-no-repeat opacity-20 z-0 pointer-events-none"
          style={{ backgroundImage: `url(${logo})` }}
        ></div>

        {/* Unified Header Section with Tabs */}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 relative z-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-white">{getUserName()}</h1>
              <div className="text-gray-300 mt-1">
        {availableTabs.length > 1 ? (
          <div className="flex items-center gap-3 mt-2">
            {availableTabs.map((tab) => {
              const { label, icon: Icon } = tabConfig[tab as TabKey];

              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab as TabKey)}
                  className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                    activeTab === tab
                      ? 'bg-[#0CB4E4]/20 text-[#0CB4E4]'
                      : 'text-gray-300 hover:text-white hover:bg-[#0CB4E4]/10'
                  }`}
                >
                  <div className="flex items-center gap-1">
                    <Icon className="w-3.5 h-3.5" />
                    <span>{label}</span>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <p>
            {role === 'viewer'
              ? 'Step into scenes crafted by fellow cr3ators—explore using your touchpad or arrow keys'
              : 'Create and manage your 3D reconstructions'}
          </p>
        )}
      </div>
            </div>
            
            {/* Mobile toggle button - only show for non-viewers or when they have content */}
            {(role !== 'viewer' || sharedScenes.length > 0) && (
              <button
                onClick={() => setShowMyScenes(prev => !prev)}
                disabled={isGloballyDisabled}
                className={`p-2 text-gray-300 hover:text-white hover:bg-[#0CB4E4]/20 rounded-lg transition-colors ml-auto sm:hidden ${
                  isGloballyDisabled ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                aria-label={showMyScenes ? "Hide create section" : "Show create section"}
                aria-expanded={showMyScenes}
              >
                {showMyScenes ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
            )}
            
            {/* Button container - Download Scan App + New Scene buttons */}
{canCreateScenes && (
  <div className="flex items-center gap-3">
    {/* Download Scan App button, commenting for now as app is under approval */}
    
    <button
      onClick={handleDownloadScanApp}
      disabled={isGloballyDisabled}
      className={`flex items-center gap-2 py-2.5 px-5 rounded-lg text-[#161513] bg-[#0CB4E4] hover:bg-[#0CB4E4]/90 shadow-md hover:shadow-lg transition-all duration-200 ${
        showMyScenes ? 'flex' : 'hidden sm:flex'
      } ${isGloballyDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      aria-label="Download scan app"
    >
   <Download className="w-5 h-5 drop-shadow-md" strokeWidth={1.5} />
      <span>Download Scan App</span>
    </button>
    
    {/* Upload Images button */}
    <button
      onClick={() => {
        resetUuid();
        setShowNewSceneForm(true); 
      }}
      disabled={isGloballyDisabled}
      className={`flex items-center gap-2 py-2.5 px-5 rounded-lg text-[#161513] bg-[#0CB4E4] hover:bg-[#0CB4E4]/90 shadow-md hover:shadow-lg transition-all duration-200 ${
        showMyScenes ? 'flex' : 'hidden sm:flex'
      } ${isGloballyDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      aria-label="Create new scene"
    >
      <Plus className="w-5 h-5 drop-shadow-md" strokeWidth={1.5} />
      <span>Upload Images</span>
    </button>
  </div>
)}
          </div>

          {/* Search and filters - disabled while loading */}
          <div className={`transition-all duration-300 ${showMyScenes || role === 'viewer' ? 'opacity-100 max-h-[2000px]' : 'opacity-0 max-h-0 overflow-hidden sm:opacity-100 sm:max-h-[2000px]'}`}>
            {/* Search and Filter Section - disabled while loading */}
            <div className="bg-[#161513]/80 backdrop-blur-sm rounded-xl shadow-lg border border-[#0CB4E4]/20 p-4 mb-6">
              <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={
                    role === 'viewer' 
                      ? "Search shared scenes..." 
                      : activeTab === 'my-collections' 
                        ? "Search my scenes..." 
                        : activeTab === 'shared-collections'
                          ? "Search shared scenes..."
                          : "Search cr3ators scenes..."
                  }
                  value={
                    activeTab === 'my-collections' ? searchTerm :
                    activeTab === 'shared-collections' ? sharedSearchTerm :
                    cr3atorsSearchTerm
                  }
                  onChange={(e) => {
                    if (activeTab === 'my-collections') {
                      setSearchTerm(e.target.value);
                    } else if (activeTab === 'shared-collections') {
                      setSharedSearchTerm(e.target.value);
                    } else {
                      setCr3atorsSearchTerm(e.target.value);
                    }
                  }}
                  disabled={isGloballyDisabled}
                  className={`w-full pl-10 pr-4 py-2.5 rounded-lg bg-[#161513]/80 backdrop-blur-sm border border-[#0CB4E4]/30 text-white focus:outline-none focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4] ${
                    isGloballyDisabled ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                />
              </div>
              <div className="flex items-center gap-4">
                <select
                  value={
                    activeTab === 'my-collections' ? sortBy :
                    activeTab === 'shared-collections' ? sharedSortBy :
                    cr3atorsSortBy
                  }
                  onChange={(e) => {
                    if (activeTab === 'my-collections') {
                      setSortBy(e.target.value as 'date' | 'quality' | 'images');
                    } else if (activeTab === 'shared-collections') {
                      setSharedSortBy(e.target.value as 'date' | 'quality' | 'images' | 'shareDate');
                    } else {
                      setCr3atorsSortBy(e.target.value as 'date' | 'quality' | 'images');
                    }
                  }}
                  disabled={isGloballyDisabled}
                  className={`px-4 py-2.5 rounded-lg bg-[#161513]/80 backdrop-blur-sm text-white border border-[#0CB4E4]/30 focus:outline-none focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4] ${
                    isGloballyDisabled ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {activeTab === 'shared-collections' ? (
                    <>
                      <option value="shareDate">Sort by Share Date</option>
                      <option value="date">Sort by Creation Date</option>
                      <option value="quality">Sort by Quality</option>
                      <option value="images">Sort by Images</option>
                    </>
                  ) : (
                    <>
                      <option value="date">Sort by Date</option>
                      <option value="quality">Sort by Quality</option>
                      <option value="images">Sort by Images</option>
                    </>
                  )}
                </select>
                <select
                  value={
                    activeTab === 'my-collections' ? filterStatus :
                    activeTab === 'shared-collections' ? sharedFilterStatus :
                    cr3atorsFilterStatus
                  }
                  onChange={(e) => {
                    if (activeTab === 'my-collections') {
                      setFilterStatus(e.target.value as Scene['status'] | 'all');
                    } else if (activeTab === 'shared-collections') {
                      setSharedFilterStatus(e.target.value as Scene['status'] | 'all');
                    } else {
                      setCr3atorsFilterStatus(e.target.value as Scene['status'] | 'all');
                    }
                  }}
                  disabled={isGloballyDisabled}
                  className={`px-4 py-2.5 rounded-lg bg-[#161513]/80 backdrop-blur-sm text-white border border-[#0CB4E4]/30 focus:outline-none focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4] ${
                    isGloballyDisabled ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <option value="all">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="processing">Processing</option>
                  <option value="pending">Pending</option>
                  <option value="error">Error</option>
                </select>
              </div>
            </div>
            </div>

            {/* My Collection section - only for non-viewers */}
            {activeTab === 'my-collections' && role !== 'viewer' && (
              <div className="mb-10">
                {isLoadingScenes ? (
                  <div className="text-center py-12 bg-[#161513]/80 backdrop-blur-sm rounded-xl shadow-lg border border-[#0CB4E4]/20">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#161513] flex items-center justify-center">
                      <div className="animate-spin">
                        <SlidersHorizontal className="w-8 h-8 text-[#0CB4E4]" />
                      </div>
                    </div>
                    <h3 className="text-lg font-medium text-white mb-2">Loading your scenes...</h3>
                    <p className="text-gray-300">Please wait while we fetch your 3D scenes</p>
                  </div>
                ) : scenesError ? (
                  <div className="text-center py-12 bg-[#161513]/80 backdrop-blur-sm rounded-xl shadow-lg border border-[#0CB4E4]/20">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#161513] flex items-center justify-center">
                      <SlidersHorizontal className="w-8 h-8 text-[#0CB4E4]" />
                    </div>
                    <h3 className="text-lg font-medium text-white mb-2">Failed to load scenes</h3>
                    <p className="text-gray-300 mb-4">{scenesError}</p>
                    {onRefreshScans && (
                      <button
                        onClick={onRefreshScans}
                        disabled={isGloballyDisabled}
                        className={`px-4 py-2 bg-[#0C5484] text-white rounded-lg hover:bg-[#0CB4E4]/80 shadow-md transition-colors ${
                          isGloballyDisabled ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        Try Again
                      </button>
                    )}
                  </div>
                ) : filteredScenes.length === 0 ? (
                  <div className="text-center py-12 bg-[#161513]/80 backdrop-blur-sm rounded-xl shadow-lg border border-[#0CB4E4]/20">
                    <img 
                      src={logo} 
                      alt="No scenes found" 
                      className="w-48 mx-auto mb-4 object-contain" 
                    />
                  
                    <p className="text-gray-300">
                      {searchTerm
                        ? 'Try adjusting your search or filters'
                        : 'Add scene images to get started or '}
                      {!searchTerm && (
                        <Link to="/the-gallery" className="text-[#0CB4E4] hover:text-[#0CB4E4]/80">
                          visit our gallery
                        </Link>
                      )}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {filteredScenes.map(scene => (
                      <SceneCard
                        key={scene.id}
                        scene={scene}
                        onClick={() => handleMySceneSelect(scene)}
                        onShare={() => handleShare(scene)}
                        onDownload={() => handleDownload(scene)}
                        onRate={() => handleRateClick(scene)}
                        onDelete={() => handleDeleteClick(scene)}
                        onTrigger={() => handleTriggerReconstruction(scene)}
                        canShare={!!canShareScenes && scene.creatorId === authUser?.uid}
                        isLoading={activeLoadingSceneId === scene.id}
                        isDisabled={isGloballyDisabled && activeLoadingSceneId !== scene.id}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Shared Collections section */}
            {activeTab === 'shared-collections' && (
              <div className="mb-10">
                {isLoadingShares ? (
                  <div className="text-center py-12 bg-[#161513]/80 backdrop-blur-sm rounded-xl shadow-lg border border-[#0CB4E4]/20">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#161513] flex items-center justify-center">
                      <div className="animate-spin">
                        <SlidersHorizontal className="w-8 h-8 text-[#0CB4E4]" />
                      </div>
                    </div>
                    <h3 className="text-lg font-medium text-white mb-2">Loading shared scenes...</h3>
                    <p className="text-gray-300">Please wait while we fetch scenes shared with you</p>
                  </div>
                ) : sharesError ? (
                  <div className="text-center py-12 bg-[#161513]/80 backdrop-blur-sm rounded-xl shadow-lg border border-[#0CB4E4]/20">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#161513] flex items-center justify-center">
                      <SlidersHorizontal className="w-8 h-8 text-[#0CB4E4]" />
                    </div>
                    <h3 className="text-lg font-medium text-white mb-2">Failed to load shared scenes</h3>
                    <p className="text-gray-300 mb-4">{sharesError}</p>
                    <button
                      onClick={loadSharedContent}
                      disabled={isGloballyDisabled}
                      className={`px-4 py-2 bg-[#0C5484] text-white rounded-lg hover:bg-[#0CB4E4]/80 shadow-md transition-colors ${
                        isGloballyDisabled ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      Try Again
                    </button>
                  </div>
                ) : filteredSharedScenes.length === 0 ? (
                  <div className="text-center py-12 bg-[#161513]/80 backdrop-blur-sm rounded-xl shadow-lg border border-[#0CB4E4]/20">
                    <img 
                      src={logo} 
                      alt="No shared scenes" 
                      className="w-48 mx-auto mb-4 object-contain" 
                    />
                    <h3 className="text-white mb-2">You're all caught up! Nothing shared here yet</h3>
                   <p className="text-gray-300">
  {sharedSearchTerm ? (
    'Try adjusting your search or filters'
  ) : (
    <Link to="/the-gallery" className="text-[#0CB4E4] hover:text-[#0CB4E4]/80">
      Visit the gallery
    </Link>
  )}
</p>
                    
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {filteredSharedScenes.map(scene => (
                      <SceneCard
                        key={scene.id}
                        scene={scene}
                        onClick={() => handleSharedSceneSelect(scene)}
                        onShare={() => handleSharedSceneShare(scene)}
                        onDownload={() => handleSharedSceneDownload(scene)}
                        onDelete={() => handleDeleteClick(scene)}
                        onTrigger={() => handleTriggerReconstruction(scene as Scene)}
                        canShare={!!canShareScenes && scene.creatorId === authUser?.uid}
                        isLoading={activeLoadingSceneId === scene.id}
                        isDisabled={isGloballyDisabled && activeLoadingSceneId !== scene.id}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Cr3ators Collections section */}
            {activeTab === 'cr3ators-collections' && (
              <div className="mb-10">
                {filteredOtherScenes.length === 0 ? (
                  <div className="text-center py-12 bg-[#161513]/80 backdrop-blur-sm rounded-xl shadow-lg border border-[#0CB4E4]/20">
                    <img 
                      src={logo}
                      alt="No cr3ator scenes" 
                      className="w-24 h-24 mx-auto mb-4 object-contain" 
                    />
                    <h3 className="text-lg font-medium text-white mb-2">No scenes found</h3>
                    <p className="text-gray-300">Try adjusting your search or filters</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {filteredOtherScenes.map(scene => (
                      <SceneCard
                        key={scene.id}
                        scene={scene}
                        onClick={() => handleMySceneSelect(scene)}
                        onShare={() => handleShare(scene)}
                        onDownload={() => handleDownload(scene)}
                        onRate={() => handleRateClick(scene)}
                        onDelete={() => handleDeleteClick(scene)}
                        onTrigger={() => handleTriggerReconstruction(scene)}
                        canShare={!!canShareScenes && scene.creatorId === authUser?.uid}
                        isLoading={activeLoadingSceneId === scene.id}
                        isDisabled={isGloballyDisabled && activeLoadingSceneId !== scene.id}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Modals - disabled while loading */}
        {showNewSceneForm && canCreateScenes && !isGloballyDisabled && (
          <NewSceneForm
            onSubmit={(sceneData) => {
              onCreateScene(sceneData);
              setShowNewSceneForm(false);
            }}
            onCancel={() => setShowNewSceneForm(false)}
          />
        )}
        
        {showFeedback && (sceneForFeedback || selectedScene) && !isGloballyDisabled && (
          <ReconstructionFeedback
            onSubmit={(feedback) => {
              onSubmitFeedback(feedback);
              setSceneForFeedback(null);
            }}
            onClose={() => {
              setShowFeedback(false);
              setSceneForFeedback(null);
            }}
          />
        )}
      </div>
      
      {/* Toast Notifications */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={handleToastClose}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && sceneToDelete && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#161513] rounded-xl shadow-xl w-full max-w-md border border-[#0CB4E4]/30 overflow-hidden">
            <div className="p-6 border-b border-[#0CB4E4]/20">
              <h3 className="text-xl font-semibold text-white">Delete Scene</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-300 mb-6">
                Are you sure you want to delete <span className="text-white font-semibold">"{sceneToDelete.name}"</span>? 
                This action cannot be undone.
              </p>
              
              <div className="bg-[#161513] p-4 border border-[#0CB4E4]/30 rounded-lg mb-6">
                <div className="flex items-center gap-3">
                  {sceneToDelete.thumbnailUrl ? (
                    <img 
                      src={sceneToDelete.thumbnailUrl} 
                      alt={sceneToDelete.name}
                      className="w-14 h-14 object-cover rounded-lg"
                    />
                  ) : (
                    <div className="w-14 h-14 bg-[#0CB4E4]/10 rounded-lg flex items-center justify-center">
                      <Cuboid className="w-6 h-6 text-[#0CB4E4]" />
                    </div>
                  )}
                  <div>
                    <div className="font-medium text-white">{sceneToDelete.name}</div>
                    <div className="text-sm text-gray-300">
                      {sceneToDelete.quality.imageCount} images • {sceneToDelete.type}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleCancelDelete}
                  className="px-4 py-2 border border-[#0CB4E4]/30 text-white hover:bg-[#161513] transition-colors rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 bg-[#0CB4E4] text-[#161513] hover:bg-[#0CB4E4]/90 transition-colors rounded-lg shadow-md"
                >
                  Delete Scene
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
